import { useEffect, useState } from "react";

/**
 * One `MediaQueryList` and one listener per distinct query, for the whole app.
 *
 * `usePrefersReducedMotion` is called by every rolling figure in the cart, so
 * mid-funnel there are sixteen or so live subscribers to a single global
 * boolean. Without this store that is sixteen `matchMedia` objects and sixteen
 * listeners — and worse, a `change` callback is not a React event, so on React
 * 17 a visitor turning reduced motion on mid-session would fire sixteen
 * unbatched `setState`s and sixteen separate render passes. Shared, it is one.
 */
type Store = {
  list: MediaQueryList;
  subscribers: Set<(matches: boolean) => void>;
};

const stores = new Map<string, Store>();

function storeFor(query: string): Store | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }
  const existing = stores.get(query);
  if (existing) return existing;

  const list = window.matchMedia(query);
  const store: Store = { list, subscribers: new Set() };
  const onChange = (event: MediaQueryListEvent) =>
    store.subscribers.forEach((notify) => notify(event.matches));

  // Safari below 14 only has the deprecated pair, and this app still serves it.
  if (typeof list.addEventListener === "function") {
    list.addEventListener("change", onChange);
  } else {
    list.addListener(onChange);
  }

  stores.set(query, store);
  return store;
}

/**
 * Whether a CSS media query currently matches.
 *
 * Distinct from `useResizeObserver`, which reports `window.screen.width` — the
 * physical display, not the window. antd's `lg` breakpoint and every layout
 * rule in `index.css` are viewport rules, so anything deciding whether the
 * sidebar has stacked has to ask the viewport. On a 1440px laptop with the
 * window dragged narrow the two disagree completely, and the screen-width
 * answer is the wrong one.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(
    () => storeFor(query)?.list.matches ?? false
  );

  useEffect(() => {
    const store = storeFor(query);
    if (!store) return;
    // Re-read on subscribe: the query can have flipped between the first render
    // and this effect, and a new subscriber must not start from a stale answer.
    setMatches(store.list.matches);
    store.subscribers.add(setMatches);
    return () => {
      store.subscribers.delete(setMatches);
    };
  }, [query]);

  return matches;
}
